import {
  Close,
  Edit,
  Facebook,
  LinkedIn,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  TextField,
  Switch,
  styled,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useRef, useState } from "react";
import Form from "../../../ui/components/Form";
import CustomTextField from "../../../ui/components/Form/TextField";
import { Instagram } from "@mui/icons-material";
import { FormatWrapInline } from "mdi-material-ui";
import { Controller } from "react-hook-form";
import { useUpdateProfilePicture } from "../../misc/api/updateProfilePicture";
import { useEditProfile } from "../api/updateProfile";

const AvatarWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  width: "fit-content",
  height: "fit-content",
  cursor: "pointer",
  ".overlay": {
    borderRadius: "999px",
    opacity: 0,
    position: "absolute",
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,

    display: "flex",
    justifyContent: "space-between",
    algnItems: "center",
    color: "white",
    backgroundColor: theme.palette.grey["600"],
    transtion: theme.transitions.create("opacity", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    svg: {
      margin: "auto",
    },
  },
  "&:hover .overlay": {
    opacity: 0.8,
  },
}));

const InputWrapper = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  ".visibility_button": {
    marginLeft: "auto",
  },
  ".input_prefix": {
    color: "#00376b",
    fontSize: "12px",
    whiteSpace: "nowrap",
  },
  ".MuiTextField-root": {
    width: "100%",
    height: "20px",
    ".MuiInput-root": {
      marginTop: "0px",
      height: "100%",
    },
  },
}));

function AvatarInput({
  formMethods,
  defaultURL = "",
  fieldName,
  avatarProps,
  inputProps,
}) {
  const { register } = formMethods;
  const inputFileRef = useRef();
  const handleClick = () => {
    inputFileRef.current.click();
  };
  const filesListObject = Array.from(formMethods.watch(fieldName) || []);
  var imageURL = defaultURL;
  if (filesListObject?.[0]) {
    imageURL = URL.createObjectURL(filesListObject[0]);
  }

  return (
    <>
      <input
        hidden
        accept="image/*"
        type="file"
        {...inputProps}
        {...register(fieldName)}
        ref={(e) => {
          register(fieldName).ref(e);
          inputFileRef.current = e;
        }}
      />
      <AvatarWrapper className="avatar_wrapper" onClick={handleClick}>
        <Avatar src={imageURL} {...avatarProps} />
        <div className="overlay">
          <Edit />
        </div>
      </AvatarWrapper>
    </>
  );
}

const schema = yup.object().shape({
  instagramURL: yup
    .string()
    .when("instagramURLVisibility", (urlVisibility, _schema) => {
      if (urlVisibility) {
        return _schema.required("Campo obrigatório");
      }
      return _schema;
    }),
  instagramURLVisibility: yup.boolean(),
  linkedinURL: yup
    .string()
    .when("linkedinURLVisibility", (urlVisibility, _schema) => {
      if (urlVisibility) {
        return _schema.required("Campo obrigatório");
      }
      return _schema;
    }),
  linkedinURLVisibility: yup.boolean(),
  facebookURL: yup
    .string()
    .when("facebookURLVisibility", (urlVisibility, _schema) => {
      if (urlVisibility) {
        return _schema.required("Campo obrigatório");
      }
      return _schema;
    }),
  facebookURLVisibility: yup.boolean(),
  profilePicture: yup.mixed().nullable(),
});

export default function EditProfile({
  children,
  defaultValues = {},
  currentProfilePicture,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const editProfileMutation = useEditProfile();

  const updateProfilePictureMutation = useUpdateProfilePicture();
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  const methods = {
    openDialog: handleOpenDialog,
  };

  const handleSubmit = (values) => {
    if (values.profilePicture?.[0]) {
      updateProfilePictureMutation.mutate({ file: values.profilePicture?.[0] });
    }
    editProfileMutation.mutate(
      {
        facebook:
          `${values.facebookURLVisibility ? 1 : 0}https://www.facebook.com/` +
          values.facebookURL,
        instagram:
          `${values.instagramURLVisibility ? 1 : 0}https://www.instagram.com/` +
          values.instagramURL.replace(/\n/g, ""),
        linkedn:
          `${
            values.linkedinURLVisibility ? 1 : 0
          }https://www.linkedin.com/in/` + values.linkedinURL,
      },
      {
        onSuccess: () => {
          handleCloseDialog();
        },
      }
    );
  };

  const theme = useTheme();
  const isDisplaySmall = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <>
      {children(methods)}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullScreen={isDisplaySmall}
      >
        <IconButton
          sx={{ position: "absolute", top: 3, right: 3 }}
          onClick={handleCloseDialog}
        >
          <Close />
        </IconButton>
        <DialogContent
          sx={{
            ".avatar_wrapper": {
              margin: "auto",
            },
          }}
        >
          <Form
            schema={schema}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
          >
            {(formMethods) => {
              return (
                <>
                  <AvatarInput
                    formMethods={formMethods}
                    fieldName={"profilePicture"}
                    defaultURL={currentProfilePicture}
                    avatarProps={{
                      sx: {
                        height: "100px",
                        width: "100px",
                      },
                    }}
                  />
                  <InputWrapper>
                    <span className="input_prefix">
                      <Instagram />
                      https://www.instagram.com/
                    </span>
                    <CustomTextField
                      formMethods={formMethods}
                      fieldName={"instagramURL"}
                      variant="standard"
                    />
                    <Stack
                      direction={"row"}
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <Controller
                        control={formMethods.control}
                        name="instagramURLVisibility"
                        render={({ field: { onChange, value } }) => (
                          <IconButton
                            sx={{
                              color: value ? "primary" : "grey",
                            }}
                            onClick={() => {
                              onChange(!value);
                            }}
                          >
                            {value ? (
                              <Visibility color="primary" />
                            ) : (
                              <VisibilityOff color="grey" />
                            )}
                          </IconButton>
                        )}
                      />
                    </Stack>
                  </InputWrapper>
                  <InputWrapper>
                    <span className="input_prefix">
                      <LinkedIn />
                      https://www.linkedin.com/in/
                    </span>
                    <CustomTextField
                      formMethods={formMethods}
                      fieldName={"linkedinURL"}
                      variant="standard"
                    />
                    <Stack
                      direction={"row"}
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <Controller
                        control={formMethods.control}
                        name="linkedinURLVisibility"
                        render={({ field: { onChange, value } }) => (
                          <IconButton
                            onClick={() => {
                              onChange(!value);
                            }}
                            sx={{
                              color: value ? "primary" : "grey",
                            }}
                          >
                            {value ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        )}
                      />
                    </Stack>
                  </InputWrapper>
                  <InputWrapper>
                    <span className="input_prefix">
                      <Facebook />
                      https://www.facebook.com/
                    </span>
                    <CustomTextField
                      formMethods={formMethods}
                      fieldName={"facebookURL"}
                      variant="standard"
                    />
                    <Stack
                      direction={"row"}
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <Controller
                        control={formMethods.control}
                        name="facebookURLVisibility"
                        render={({ field: { onChange, value } }) => (
                          <IconButton
                            sx={{
                              color: value ? "primary" : "grey",
                            }}
                            onClick={() => {
                              onChange(!value);
                            }}
                          >
                            {value ? (
                              <Visibility color="primary" />
                            ) : (
                              <VisibilityOff color="grey" />
                            )}
                          </IconButton>
                        )}
                      />
                    </Stack>
                  </InputWrapper>
                  <Button type="submit">Salvar</Button>
                </>
              );
            }}
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
